import z from 'zod';

export const AUS_STATES = [
    'New South Wales',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
    'Australian Capital Territory',
    'Northern Territory',
] as const
export const stateQualification: {
    [state: string]: { school: string | null; earlyChildcare: string | null }
} = {
    Victoria: { school: 'VIT', earlyChildcare: 'WWCC' },
    'New South Wales': { school: 'NESA', earlyChildcare: 'WWCC' },
    Queensland: { school: 'QCT', earlyChildcare: 'Blue card' },
    'South Australia': { school: 'TRB', earlyChildcare: 'WWCC' },
    'Western Australia': { school: 'TRB', earlyChildcare: 'WWCC' },
    Tasmania: { school: 'TRB', earlyChildcare: 'WWCC' },
    'Australian Capital Territory': { school: 'TQI', earlyChildcare: 'WWVP' },
    'Northern Territory': { school: 'TRB', earlyChildcare: 'WWVP' },
}

export type ReferenceSurveySteps_v3 = 'start' | 'form' | 'end-disagree' | 'end-agree'

export const locationStateToRegMap = {
    Victoria: [
        { label: 'I have a current VIT', value: 'vit' },
        { label: 'I have a current WWCC', value: 'wwcc' },
    ],
    'New South Wales': [
        { label: 'I have a current NESA', value: 'nesa' },
        { label: 'I have a current WWCC', value: 'wwcc' },
    ],
    Queensland: [
        { label: 'I have a current QCT', value: 'qct' },
        { label: 'I have a current Blue card', value: 'blueCard' },
    ],
    'South Australia': [
        { label: 'I have a current TRB', value: 'trb' },
        { label: 'I have a current WWCC', value: 'wwcc' },
    ],
    'Western Australia': [
        { label: 'I have a current TRB', value: 'trb' },
        { label: 'I have a current WWCC', value: 'wwcc' },
    ],
    Tasmania: [
        { label: 'I have a current TRB', value: 'trb' },
        { label: 'I have a current WWCC', value: 'wwcc' },
    ],
    'Australian Capital Territory': [
        { label: 'I have a current TQI', value: 'tqi' },
        { label: 'I have a current WWVP', value: 'wwvp' },
    ],
    'Northern Territory': [
        { label: 'I have a current TRB', value: 'trb' },
        { label: 'I have a current WWVP', value: 'wwvp' },
    ],
}

const refereeDetailsSchema = z.object({
    referenceNumber: z.string().min(1, 'Incomplete*').default(''),
    supportDocument: z.file(),
})
const candidateDetailsSchema = z.object({
    fullName: z
        .string()
        .min(1, 'Incomplete*')
        .default('')
        .describe(`Candidate's full name`),
    relationship: z
        .string()
        .min(1, 'Incomplete*')
        .default('')
        .describe(`Referee'sWorking relationship with candidate`),
    relationshipLength: z
        .string()
        .min(1, 'Incomplete*')
        .default('')
        .describe('Length referee has known candidate for'),
    workedWithLength: z
        .string()
        .min(1, 'Incomplete*')
        .default('')
        .describe('Length referee worked with candidate for'),
})
const candidateSuitabilitySchema = z.object({
    score: z.number().min(1).max(10),
    employCandidate: z.string().min(1, 'Incomplete*'),
    otherDetails: z.string().optional().default(''),
})

export const referenceSurveySchema = z.object({
    fullName: z.string().min(1, 'Incomplete*').default(''),
    locationState: z.enum(AUS_STATES, 'Incomplete*'),

    data: z.discriminatedUnion('role', [
        z.object({
            role: z.enum(['crt', 'es'] as const),
            refereeDetails: refereeDetailsSchema.partial({
                supportDocument: true,
            }),
            candidateDetails: candidateDetailsSchema,
            suitability: candidateSuitabilitySchema,
        }),
        z.object({
            role: z.literal('ect'),
            refereeDetails: refereeDetailsSchema.partial({
                supportDocument: true,
            }),
            candidateDetails: candidateDetailsSchema.extend({
                hasWorkedWith: z
                    .enum(['yes', 'no'] as const)
                    .describe(
                        'Whether referee has worked with this candidate in an early childhood environment'
                    ),
            }),
            suitability: candidateSuitabilitySchema,
        }),
        z.object({
            role: z.literal('non-ect'),
            refereeDetails: refereeDetailsSchema,
            candidateDetails: candidateDetailsSchema.extend({
                hasWorkedWith: z
                    .enum(['yes', 'no'] as const)
                    .describe(
                        'Whether referee has worked with this candidate in an early childhood environment'
                    ),
            }),
            suitability: candidateSuitabilitySchema,
        }),
    ]),
})
export type ReferenceSurveySubmission = z.infer<typeof referenceSurveySchema>
